import { AutoSuggestDocument } from '@careerhub/api';
import {
  CareerHubModuleReactContext,
  LoadingStates,
  SectionError,
  SectionLoader,
  careerStateSelector,
} from '@careerhub/common';
import {
  Accordion,
  AccordionHeader,
  AccordionItem,
  AccordionToggleEventHandler,
  Body1Strong,
  SelectTabData,
  Subtitle2,
  Tab,
  TabList,
  TabValue,
  mergeClasses,
  Input,
  tokens,
  MenuPopover,
  MenuTrigger,
  MenuList,
  MenuItem,
  Menu,
} from '@fluentui/react-components';
import { useHistory } from 'react-router-dom';

import * as React from 'react';
import { FeatureName, SubFeatureName } from '../resources/TelemetryEventDetails';
import { useLevelOneStyles, useTabStyles } from './LevelOne.styles';
import { ILevelOneProps } from './LevelOne.types';
import { ProfessionAccordionPanel } from './LevelOneAccordionPanel';
import { Bookmarks } from '../Bookmarks/BookmarksV2';
import { ErrorContent } from '../resources/resources';
import { useSelector } from 'react-redux';
import { Search16Regular } from '@fluentui/react-icons';

export const LevelOnePage: React.FC = () => {
  const styles = useLevelOneStyles();
  const tabStyles = useTabStyles();
  const [apiCallLoadingState, setApiCallLoadingState] = React.useState(LoadingStates.NOT_STARTED);
  const [openItems, setOpenItems] = React.useState<string[]>([]);
  const [level1Taxonomy, setLevel1Taxonomy] = React.useState<ILevelOneProps[]>([]);
  const [selectedValue, setSelectedValue] = React.useState<TabValue>('All');
  const [text, setText] = React.useState<string>('');
  const [autoSuggestion, setAutoSuggestion] = React.useState<AutoSuggestDocument[]>([]);
  const [announceText, setAnnounceText] = React.useState('');
  const { telemetryClient, roleLibraryService } = React.useContext(CareerHubModuleReactContext);
  const { isChatOpen, taxonomyTypes, fetchTaxonomyTypeStatus } = useSelector(careerStateSelector);
  const menuItemRef = React.useRef<HTMLDivElement>(null);
  const searchBarRef = React.useRef<HTMLInputElement>(null);
  const bookmarkRef = React.useRef<HTMLButtonElement>(null);
  const history = useHistory();

  const onTabSelect = (_: unknown, data: SelectTabData) => {
    telemetryClient.logButtonClick(FeatureName.BrowseProfession, SubFeatureName.TabList, { tabName: data.value });
    setSelectedValue(data.value);
  };

  const fetchLevel1Data = React.useCallback(() => {
    setApiCallLoadingState(LoadingStates.STARTED);
    if (apiCallLoadingState != LoadingStates.SUCCEEDED) {
      const level1Type = taxonomyTypes.find((item) => item.level === 1)?.id || 'Profession';
      roleLibraryService
        .getTaxonomicRank(level1Type, '')
        .then((data) => {
          const level1Data: ILevelOneProps[] = data.map((level1Item) => {
            return {
              id: level1Item.id,
              name: level1Item.name,
              children: level1Item.childTaxonomyCount.toString(),
              route: `/${level1Type}`,
              isProfessionPage: false,
            };
          });
          setApiCallLoadingState(LoadingStates.SUCCEEDED);
          setLevel1Taxonomy(level1Data);
        })
        .catch((_) => {
          setApiCallLoadingState(LoadingStates.FAILED);
        });
    }
  }, [apiCallLoadingState, taxonomyTypes, roleLibraryService]);

  React.useEffect(() => {
    if (apiCallLoadingState == LoadingStates.NOT_STARTED && fetchTaxonomyTypeStatus == LoadingStates.SUCCEEDED)
      fetchLevel1Data();
  }, [apiCallLoadingState, fetchLevel1Data, fetchTaxonomyTypeStatus]);

  const handleSearchClick = (suggestion: AutoSuggestDocument) => {
    const level3Type = taxonomyTypes.find((item) => item.level === 3)?.id || 'Role';
    history.push(`/${level3Type}/${suggestion.id}`);
  };

  const handleToggle: AccordionToggleEventHandler<string> = (_event, data) => {
    if (data.openItems.length > openItems.length) {
      telemetryClient.logDropDownSelected(FeatureName.BrowseProfession, SubFeatureName.ExpandProfession, {
        professionId: data.value,
      });
    }
    setOpenItems(data.openItems);
  };

  const renderProfessions = (professions: ILevelOneProps[]) => {
    professions = [...professions].sort((a, b) => {
      if (!a?.name && !b?.name) return 0;
      if (!a?.name) return 1;
      if (!b?.name) return -1;
      return a.name.localeCompare(b.name);
    });

    const midpoint = Math.ceil(professions.length / 2);

    return (
      <>
        <div style={{ gridColumn: 1 }}>{renderProfessionsColumn(professions.slice(0, midpoint))}</div>
        <div style={{ gridColumn: 2 }}>{renderProfessionsColumn(professions.slice(midpoint))}</div>
      </>
    );
  };

  const renderProfessionsColumn = (professions: ILevelOneProps[]) => {
    return professions?.map((level1HierarchyDataSource) => {
      return (
        <AccordionItem
          key={level1HierarchyDataSource.id}
          value={level1HierarchyDataSource.id}
          className={styles.accordionItem}
          role="listitem"
        >
          <AccordionHeader expandIconPosition="end">
            <Body1Strong>{level1HierarchyDataSource.name}</Body1Strong>
          </AccordionHeader>
          {openItems.includes(level1HierarchyDataSource.id ?? '') && (
            <ProfessionAccordionPanel {...level1HierarchyDataSource} />
          )}
        </AccordionItem>
      );
    });
  };
  return (
    <div className={styles.sectionContainer}>
      <div style={{ display: 'flex', justifyContent: 'space-between' }}>
        <Subtitle2 as="h2">Browse professions</Subtitle2>
        <Menu
          positioning="below"
          closeOnScroll={true}
          open={text.length > 0}
          onOpenChange={(_e, data) => {
            //If there is an outside click, clear the search bar and close the menu
            if (data.open == false && data.type == 'clickOutside') {
              setText('');
            }
            //If the menu is opened and the trigger is a click, focus on the search bar
            // This is needed because sometime the focus is going directly to the dropdown menu instead of search bar.
            if (data.open == true && data.type == 'menuTriggerClick') {
              searchBarRef.current?.focus();
            }
          }}
        >
          <MenuTrigger disableButtonEnhancement>
            <Input
              contentBefore={<Search16Regular style={{ color: tokens.colorPaletteBlueBorderActive }} />}
              type="search"
              role="searchbox"
              aria-haspopup={undefined}
              aria-expanded={undefined}
              value={text}
              className={styles.searchInput}
              placeholder="Search for roles"
              ref={searchBarRef}
              onChange={async (_e, d) => {
                const specialCharactersRegex = /[!@#$%^&*(),.?":{}|<>]/g;
                const str = d.value;
                setText(str || '');
                if (str != null && str.trim().length > 0 && !specialCharactersRegex.test(str)) {
                  setAnnounceText('');
                  try {
                    const suggestions = await roleLibraryService.getAutoSuggestedRoles(str, 'Role');
                    const resultText =
                      suggestions.length > 0
                        ? `${suggestions.length} result${suggestions.length === 1 ? '' : 's'} available`
                        : 'No matches found';
                    setAutoSuggestion(suggestions);
                    setAnnounceText(resultText);
                  } catch (error) {
                    console.log('an error occured calling getAutoSuggestedRoles: ', error);
                  }
                } else {
                  setAutoSuggestion([]);
                  setAnnounceText('');
                }
              }}
              aria-label={`search box${autoSuggestion.length > 0 ? ' press key up or down to navigate roles' : ''}`}
              onKeyDown={(event) => {
                if (event.key === 'ArrowDown') {
                  menuItemRef.current?.focus();
                } else if (event.key === 'Enter' && autoSuggestion.length > 0) {
                  handleSearchClick(autoSuggestion[0]);
                } else if (event.key === 'Escape') {
                  setAutoSuggestion([]);
                  setAnnounceText('');
                }
              }}
            />
          </MenuTrigger>
          <MenuPopover className={styles.popoverContainer}>
            {text?.length > 0 ? (
              <div>
                {autoSuggestion.length > 0 ? (
                  <MenuList
                    className={mergeClasses(styles.quickSearchPopover, styles.quickSearchPopoverSpacing)}
                    role="list"
                    aria-label="Search results"
                  >
                    {autoSuggestion.map((suggestion, index) => (
                      <MenuItem
                        ref={index === 0 ? menuItemRef : undefined}
                        role="listitem"
                        aria-posinset={index + 1}
                        aria-setsize={autoSuggestion.length}
                        aria-label={`${suggestion.name} role`}
                        className={styles.quickSearchLink}
                        onClick={() => handleSearchClick(suggestion)}
                        onKeyDown={(event) => {
                          if (event.key === 'Escape') {
                            searchBarRef.current?.focus();
                          }
                        }}
                      >
                        {suggestion.name}
                      </MenuItem>
                    ))}
                  </MenuList>
                ) : (
                  <div>No matches found</div>
                )}
              </div>
            ) : (
              <></>
            )}
          </MenuPopover>
        </Menu>
        <div role="status" aria-live="polite" aria-atomic="true" className={styles.srOnly}>
          {announceText}
        </div>
      </div>
      <div className={tabStyles.root}>
        <TabList selectedValue={selectedValue} reserveSelectedTabSpace={false} onTabSelect={onTabSelect}>
          <Tab value="All" style={{ marginLeft: '-10px' }} className={tabStyles.tabName}>
            <span className={selectedValue == 'All' ? tabStyles.selectedTabName : tabStyles.unSelectedTabName}>
              All
            </span>
          </Tab>
          <Tab value="Bookmarked" className={tabStyles.tabName} ref={bookmarkRef}>
            <span className={selectedValue == 'Bookmarked' ? tabStyles.selectedTabName : tabStyles.unSelectedTabName}>
              Bookmarked
            </span>
          </Tab>
        </TabList>
        {selectedValue == 'All' && (
          <SectionLoader
            announce
            errorComponent={
              <SectionError
                title={ErrorContent.sectionErrorTitle}
                description={ErrorContent.sectionErrorDescription}
                className={styles.error}
              />
            }
            state={apiCallLoadingState}
            error={{
              onRefresh: fetchLevel1Data,
            }}
          >
            <Accordion
              className={isChatOpen ? styles.levelOneContainerOnAssistExpand : styles.levelOneContainer}
              role="list"
              aria-label="Professions"
              multiple
              collapsible
              onToggle={handleToggle}
            >
              {renderProfessions(level1Taxonomy || [])}
            </Accordion>
          </SectionLoader>
        )}
        {selectedValue === 'Bookmarked' && <Bookmarks handleFocus={() => bookmarkRef.current?.focus()} />}
      </div>
    </div>
  );
};
